import { useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_FILTERED_VEHICLES, VEHICLE_DETAILS } from '../schemas/VehicleDetailSchema';
import { QUERY_VERSION } from '../constants';
import { VehicleDetails, VehicleBag } from '../types';

type UseVehicleDetails = (
  vehicleId: string | null,
  market: string | null,
  isTier3?: boolean,
) => VehicleBag;

interface QueryResult {
  getVehicleInfoForWormwood: VehicleDetails;
}

const useVehicleDetails: UseVehicleDetails = (vehicleId, market, isTier3) => {
  const { i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';

  const isValidVehicleId = useMemo(() => {
    return vehicleId?.length && vehicleId.length > 17 && vehicleId.endsWith('=');
  }, [vehicleId]);

  const [
    getVehicleDetails,
    { data: vehicleDetailsData, loading: loadingDetails, error: errorDetails },
  ] = useLazyQuery(VEHICLE_DETAILS, { errorPolicy: 'all' });

  const [
    getFilteredVehicles,
    { data: filteredVehiclesData, loading: loadingFiltered, error: errorFiltered },
  ] = useLazyQuery(GET_FILTERED_VEHICLES, {
    errorPolicy: 'all',
    onCompleted: (data) => {
      const vehicle = data?.getFilteredVehiclesForWormwood?.vehicles?.[0];
      const totalCount = data?.getFilteredVehiclesForWormwood?.filterResults?.totalCount;
      const searchedVehicleId = vehicle?.id;
      // If total count is more than 2 then something is wrong so skip
      if (vehicleId && searchedVehicleId && totalCount < 5 && totalCount !== 0) {
        getVehicleDetails({
          variables: {
            version: QUERY_VERSION,
            market: market?.toUpperCase(),
            lang,
            id: searchedVehicleId,
          },
        });
      }
    },
  });

  // To split when we need JDP data, and when DDC
  // ------------------------------------------------------------------------------------
  const importDb = useMemo(() => {
    const isUsMarket = market === 'us' || market === 'usuc';
    const isUsedVehicles = market?.includes('uc');

    // CA and US T3 and Mock Vehicles crated by NARWHAL team
    // if (isTier3page && dataSource === 'mock') return 'AGC_USA_NARWHAL';
    if (isTier3) {
      return isUsMarket ? 'AGC_USA_JDP' : 'AGC_CAN_JDP'; // CA OR US T3 and source JDP
    }
    if (isUsMarket) return 'TAMUSNP'; // US T1 or US T3 with default dataSource
    if (isUsedVehicles) return 'X'; // CA Used T1 or CA Used T3 with default dataSource
    return 'CANN'; // CA NEW T1 or CA NEW T3 with default dataSource
  }, [isTier3, market]);

  useEffect(() => {
    if (!vehicleId || !market) return;
    if (isValidVehicleId) {
      getVehicleDetails({
        variables: {
          version: QUERY_VERSION,
          market: market?.toUpperCase(),
          lang,
          id: vehicleId,
        },
      });
      return;
    }
    getFilteredVehicles({
      variables: {
        version: QUERY_VERSION,
        market: [market?.toUpperCase()],
        lang: 'en',
        filters: `t_vin.${vehicleId}`,
        ranges: '',
        // we don't need the actual vehicle list in the filter drawer,
        // so we set limit=0 in this case to speed up the process
        limit: 1,
        offset: 0,
        preset: `stat-import.${importDb}`,
      },
    });
  }, [getFilteredVehicles, getVehicleDetails, importDb, isValidVehicleId, lang, market, vehicleId]);

  const vehicleData = vehicleDetailsData?.getVehicleInfoForWormwood;
  const loading = loadingDetails || loadingFiltered;
  const error = errorDetails || errorFiltered;

  return { vehicle: vehicleData, loading, market, oaddDealerId: undefined };
};

export default useVehicleDetails;
